import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { toneForPriorityLevel } from '../tickets/presentation';
import { useMasterData } from './queries';
import { usePriorityMatrix, useReplacePriorityMatrix } from './queries';
import { Select } from '../../components/Select';

/**
 * Der Matrixeditor -- K-5.
 *
 * Ein Raster: Auswirkung in den Zeilen, Dringlichkeit in den Spalten, in jeder
 * Zelle eine Prioritaet. Genau so haengt die Matrix bei den meisten Kunden an
 * der Wand, und genau so laesst sie sich in einem Zug ausfuellen.
 *
 * Gespeichert wird immer das **ganze** Raster. Zellenweises Speichern fuehrte
 * zu Matrizen mit Loechern, und eine Luecke waere ein Ticket, dessen
 * Prioritaet sich nicht ermitteln laesst.
 */
export function PriorityMatrixPage() {
  const { t } = useTranslation();
  const matrix = usePriorityMatrix();
  const priorities = useMasterData('priority');
  const save = useReplacePriorityMatrix();

  const [cells, setCells] = useState<Record<string, string>>({});

  // Der lokale Stand wird aus dem Server aufgebaut, sobald er da ist. Danach
  // bleibt er stehen, damit die Eingaben beim Nachladen nicht verschwinden.
  useEffect(() => {
    if (!matrix.data) return;
    setCells(
      Object.fromEntries(
        matrix.data.cells.map((cell) => [`${cell.impact_id}:${cell.urgency_id}`, cell.priority_id]),
      ),
    );
  }, [matrix.data]);

  const complete = useMemo(() => {
    if (!matrix.data) return false;
    return matrix.data.impacts.every((impact) =>
      matrix.data.urgencies.every((urgency) => Boolean(cells[`${impact.id}:${urgency.id}`])),
    );
  }, [matrix.data, cells]);

  if (matrix.isPending || priorities.isPending) return <SkeletonTable columns={2} />;
  if (matrix.isError) return <ErrorNotice error={matrix.error} />;
  if (priorities.isError) return <ErrorNotice error={priorities.error} />;

  const { impacts, urgencies, enabled } = matrix.data;

  if (impacts.length === 0 || urgencies.length === 0) {
    return (
      <>
        <h1>{t('matrix.title')}</h1>
        <div className="card">
          <EmptyState icon="matrix" title={t('matrix.noAxes')} hint={t('matrix.noAxesHint')} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('matrix.title')}
        subtitle={t('matrix.subtitle')}
        action={
          <Button
            variant="primary"
            disabled={!complete}
            busy={save.isPending}
            onClick={() => {
              save.mutate(
                Object.entries(cells).map(([key, priorityId]) => {
                  const [impactId, urgencyId] = key.split(':');
                  return {
                    impact_id: impactId ?? '',
                    urgency_id: urgencyId ?? '',
                    priority_id: priorityId,
                  };
                }),
              );
            }}
          >
            {t('matrix.save')}
          </Button>
        }
      />

      <p style={{ marginBottom: 'var(--space-4)' }}>
        <Badge tone={enabled ? 'success' : 'neutral'} dot>
          {enabled ? t('matrix.enabled') : t('matrix.disabled')}
        </Badge>
      </p>

      {save.isError ? <ErrorNotice error={save.error} /> : null}
      {!complete ? (
        <div className="notice notice--warning">
          <span>{t('matrix.incomplete')}</span>
        </div>
      ) : null}

      <div className="card card--flush">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">
                  {t('matrix.impact')} \ {t('matrix.urgency')}
                </th>
                {urgencies.map((urgency) => (
                  <th key={urgency.id} scope="col">
                    {urgency.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {impacts.map((impact) => (
                <tr key={impact.id}>
                  <th scope="row" style={{ textAlign: 'left' }}>
                    {impact.name}
                  </th>
                  {urgencies.map((urgency) => {
                    const key = `${impact.id}:${urgency.id}`;
                    const selected = cells[key] ?? '';
                    const priority = priorities.data.find((item) => item.id === selected);

                    return (
                      <td key={urgency.id}>
                        <Select
                          className="field__control"
                          aria-label={`${impact.name} / ${urgency.name}`}
                          value={selected}
                          style={{ minWidth: '9rem' }}
                          onChange={(event) => {
                            setCells((current) => ({ ...current, [key]: event.target.value }));
                          }}
                        >
                          <option value="">{t('app.none')}</option>
                          {priorities.data.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </Select>
                        {priority ? (
                          <div style={{ marginTop: 'var(--space-1)' }}>
                            <Badge tone={toneForPriorityLevel(Number(priority.level))}>
                              {priority.name}
                            </Badge>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
